import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n/i18n';
import { localize } from './i18n/locale';
import { HelmetProvider } from 'react-helmet-async';
import { getMyStats, syncPendingResults } from './client';
import { hasStoredToken, hydrateAuthState, setToken } from './auth/auth';
import {
  clearPendingReturnTo,
  consumePendingReturnTo,
  getPostLoginRedirectPath,
  sanitizeReturnTo,
} from './auth/returnTo';
import NavBar from './components/NavBar.jsx';
import Footer from './components/Footer.jsx';
import Home from './pages/Home.jsx';
import About from './pages/About.jsx';
import AuthPage from './pages/auth/AuthPage.jsx';
import MyPage from './pages/mypage/MyPage.jsx';
import FaceResultDetail from './pages/mypage/FaceResultDetail.jsx';
import TotalAnalysis from './pages/mypage/TotalAnalysis.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import Main from './pages/Teto_vs_egen/Main.jsx';
import Info from './pages/Teto_vs_egen/Info.jsx';
import PrivacyPolicy from './pages/legal/PrivacyPolicy.jsx';
import TermsOfService from './pages/legal/TermsOfService.jsx';
import RefundPolicy from './pages/legal/RefundPolicy.jsx';
import ChatPage from './pages/ChatPage.jsx';
import ChugumiIntro from './pages/ChugumiTest/Intro.jsx';
import ChugumiQuestion from './pages/ChugumiTest/Question.jsx';
import ChugumiLoading from './pages/ChugumiTest/Loading.jsx';
import ChugumiResult from './pages/ChugumiTest/Result.jsx';
import CharacterIntro from './pages/CharacterTest/Intro.jsx';
import CharacterQuestion from './pages/CharacterTest/Question.jsx';
import CharacterResult from './pages/CharacterTest/Result.jsx';
import SebIntro from './pages/SebTest/Intro.jsx';
import SebQuestion from './pages/SebTest/Question.jsx';
import SebLoading from './pages/SebTest/Loading.jsx';
import SebResult from './pages/SebTest/Result.jsx';
import MegaIntro from './pages/MegaTest/Intro.jsx';
import MegaQuestion from './pages/MegaTest/Question.jsx';
import MegaLoading from './pages/MegaTest/Loading.jsx';
import MegaResult from './pages/MegaTest/Result.jsx';
import BeyondIntro from './pages/BeyondTest/Intro.jsx';
import BeyondQuestion from './pages/BeyondTest/Question.jsx';
import BeyondLoading from './pages/BeyondTest/Loading.jsx';
import BeyondResult from './pages/BeyondTest/Result.jsx';
import ResultPage from './pages/result/ResultPage.jsx';
import ChemistryIntro from './pages/ChemistryTest/Intro.jsx';
import ChemistryInfo from './pages/ChemistryTest/Info.jsx';
import ChemistryLoading from './pages/ChemistryTest/Loading.jsx';
import ChemistryResult from './pages/ChemistryTest/Result.jsx';
import AdminDashboard from './pages/admin/AdminDashboard.jsx';
import AdminUserDetail from './pages/admin/AdminUserDetail.jsx';
import PaidVersion from './pages/PaidVersion.jsx';
import BeyondPaidVersion from './pages/BeyondPaidVersion.jsx';
import InsideReport from './pages/InsideReport.jsx';
import PaymentStart from './pages/payments/PaymentStart.jsx';
import PaymentSuccess from './pages/payments/PaymentSuccess.jsx';
import PaymentFail from './pages/payments/PaymentFail.jsx';
import HarmonyIntro from './pages/Harmony/HarmonyIntro.jsx';
import HarmonySelect from './pages/Harmony/HarmonySelect.jsx';
import HarmonyResult from './pages/Harmony/HarmonyResult.jsx';
import HarmonyLobby from './pages/Harmony/HarmonyLobby.jsx';
import CollaborativeResult from './pages/Harmony/CollaborativeResult.jsx';
import InsightsList from './pages/Insights/InsightsList.jsx';
import InsightsDetail from './pages/Insights/InsightsDetail.jsx';
import Pricing from './pages/Pricing.jsx';
import StyleCheck from './pages/StyleCheck/StyleCheck.jsx';
import StyleBeta from './pages/StyleBeta/StyleBeta.jsx';
import StyleBetaReward from './pages/StyleBeta/StyleBetaReward.jsx';
import { useMarket } from './payments/useMarket';
import {
  addAppResumeListener,
  addAppUrlOpenListener,
  clearAuthFlow,
  closeAuthSession,
  hasProcessedAuthCallback,
  isAuthFlowExpired,
  markAuthCallbackProcessed,
  markAuthFlowResult,
  parseAuthCallbackUrl,
  readPendingAuthFlow,
} from './config/capacitor';
import {
  initAnalytics,
  normalizeSourcePage,
  setAnalyticsContext,
  setAnalyticsUserProperties,
  trackEventOnce,
  trackPageView,
} from './utils/analytics';

function AppLayout() {
  return (
    <div>
      <NavBar />
      <main className="app-main">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function AuthToast({ toast }) {
  if (!toast?.message) {
    return null;
  }

  const backgroundByTone = {
    info: 'rgba(59, 130, 246, 0.92)',
    success: 'rgba(16, 185, 129, 0.92)',
    warning: 'rgba(245, 158, 11, 0.94)',
    error: 'rgba(239, 68, 68, 0.94)',
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(var(--safe-area-top, 0px) + 16px)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        maxWidth: 'min(92vw, 560px)',
        width: 'max-content',
        background: backgroundByTone[toast.tone] || backgroundByTone.info,
        color: '#fff',
        padding: '12px 16px',
        borderRadius: '14px',
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.28)',
        fontSize: '0.95rem',
        fontWeight: 600,
        lineHeight: 1.4,
        textAlign: 'center',
      }}
    >
      {toast.message}
    </div>
  );
}

function AnalyticsTracker() {
  const location = useLocation();
  const { i18n: analyticsI18n } = useTranslation();
  const { loading: marketLoading, data: marketData } = useMarket();
  const appLanguage = String(analyticsI18n.resolvedLanguage || analyticsI18n.language || 'ko').startsWith('en') ? 'en' : 'ko';

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    if (marketLoading) return;

    const isAuthenticated = hasStoredToken();
    setAnalyticsContext({
      app_language: appLanguage,
      locale: appLanguage,
      market: marketData.market,
      is_logged_in: isAuthenticated,
      source_page: normalizeSourcePage(location.pathname),
    });
    setAnalyticsUserProperties({
      app_language: appLanguage,
      market: marketData.market,
      login_status: isAuthenticated ? 'logged_in' : 'logged_out',
    });
    trackPageView(location.pathname);
    if (location.pathname === '/') {
      trackEventOnce('landing_view', 'home:' + marketData.market + ':' + appLanguage, {
        market: marketData.market, locale: appLanguage, source_page: 'home',
      });
    }
  }, [appLanguage, location.pathname, marketData.market, marketLoading]);

  return null;
}

function authCopy(ko, en) {
  return localize(i18n.resolvedLanguage || i18n.language, { ko, en });
}

function getAuthErrorMessage(payload, pendingFlow) {
  if (payload.parseError === 'invalid_callback_url') {
    return authCopy('로그인 콜백 주소를 해석하지 못했습니다.', 'Could not parse the login callback URL.');
  }

  if (payload.parseError === 'invalid_state') {
    return authCopy('로그인 상태를 확인하지 못했습니다.', 'The login state could not be verified.');
  }

  if (payload.error === 'missing_code') {
    return authCopy('로그인 승인 코드가 전달되지 않았습니다.', 'The OAuth callback did not include an authorization code.');
  }

  if (payload.error === 'invalid_state' || payload.error === 'state_mismatch') {
    return authCopy('로그인 요청 상태가 일치하지 않습니다.', 'The login request and callback state did not match.');
  }

  if (payload.error === 'access_denied') {
    return authCopy('로그인이 취소되었습니다.', 'Login was cancelled.');
  }

  if (payload.errorDescription) {
    return payload.errorDescription;
  }

  if (pendingFlow?.provider) {
    return authCopy(`${pendingFlow.provider} 로그인에 실패했습니다. 다시 시도해 주세요.`, `${pendingFlow.provider} login failed. Please try again.`);
  }

  return authCopy('로그인을 완료하지 못했습니다. 다시 시도해 주세요.', 'Could not complete login. Please try again.');
}

async function completePostLogin(token, navigate, showToast, redirectPath = '/mypage') {
  setToken(token);

  try {
    await syncPendingResults();
  } catch (error) {
    console.warn('Failed to sync pending results after OAuth login:', error);
  }

  const profile = await getMyStats();
  if (!profile) {
    showToast(authCopy('로그인은 완료됐지만 프로필을 불러오지 못했습니다. 마이페이지에서 다시 시도해 주세요.', 'Login finished, but profile data could not be loaded. Please try again in My Page.'), 'warning');
  } else {
    showToast(authCopy('로그인이 완료되었습니다.', 'Login completed.'), 'success');
  }

  const nextPath = consumePendingReturnTo(redirectPath, '/mypage');
  navigate(nextPath, { replace: true });
}

function AuthBootstrap({ showToast }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isHandlingRef = useRef(false);
  const lastHandledCallbackIdRef = useRef('');

  useEffect(() => {
    const handleAuthFeedback = (event) => {
      const detail = event?.detail || {};
      if (detail.message) {
        showToast(detail.message, detail.tone || 'info');
      }
    };

    window.addEventListener('auth-toast', handleAuthFeedback);
    return () => window.removeEventListener('auth-toast', handleAuthFeedback);
  }, [showToast]);

  useEffect(() => {
    let cancelled = false;

    const processCallback = async (urlString, source) => {
      const payload = parseAuthCallbackUrl(urlString);
      if (!payload.shouldHandle) {
        return;
      }

      if (
        payload.callbackId &&
        (payload.callbackId === lastHandledCallbackIdRef.current ||
          hasProcessedAuthCallback(payload.callbackId))
      ) {
        return;
      }

      if (isHandlingRef.current) {
        return;
      }

      isHandlingRef.current = true;
      const pendingFlow = readPendingAuthFlow();
      const resolvedReturnTo = getPostLoginRedirectPath({
        oauthReturnTo: payload.returnTo,
        pendingFlowReturnTo: pendingFlow?.returnTo,
        location,
        fallback: '/mypage',
      });
      const safeRedirectPath = sanitizeReturnTo(
        source === 'native' && !pendingFlow ? '' : resolvedReturnTo,
        '/mypage'
      );

      try {
        if (payload.parseError) {
          markAuthFlowResult('failed', getAuthErrorMessage(payload, pendingFlow));
          showToast(getAuthErrorMessage(payload, pendingFlow), 'error');
          await closeAuthSession();
          clearPendingReturnTo();
          navigate('/mypage', { replace: true });
          return;
        }

        if (pendingFlow && payload.stateNonce && pendingFlow.stateNonce !== payload.stateNonce) {
          const mismatchPayload = { ...payload, error: 'state_mismatch' };
          markAuthFlowResult('failed', getAuthErrorMessage(mismatchPayload, pendingFlow));
          showToast(getAuthErrorMessage(mismatchPayload, pendingFlow), 'error');
          await closeAuthSession();
          clearPendingReturnTo();
          navigate('/mypage', { replace: true });
          return;
        }

        if (payload.error) {
          markAuthFlowResult('failed', getAuthErrorMessage(payload, pendingFlow));
          showToast(
            getAuthErrorMessage(payload, pendingFlow),
            payload.error === 'access_denied' ? 'warning' : 'error'
          );
          await closeAuthSession();
          clearPendingReturnTo();
          navigate('/mypage', { replace: true });
          return;
        }

        if (!payload.token) {
          const missingTokenPayload = { ...payload, error: 'missing_token' };
          markAuthFlowResult('failed', getAuthErrorMessage(missingTokenPayload, pendingFlow));
          showToast(authCopy('로그인 콜백에 액세스 토큰이 없습니다.', 'Login callback returned without an access token.'), 'error');
          await closeAuthSession();
          clearPendingReturnTo();
          navigate('/mypage', { replace: true });
          return;
        }

        markAuthFlowResult('completed');
        markAuthCallbackProcessed(payload);
        lastHandledCallbackIdRef.current = payload.callbackId || '';
        await closeAuthSession();

        if (!cancelled) {
          await completePostLogin(payload.token, navigate, showToast, safeRedirectPath);
        }
      } catch (error) {
        console.error(`Failed to process ${source} auth callback:`, error);
        markAuthFlowResult('failed', 'Login callback processing failed.');
        showToast(authCopy('로그인 콜백 처리에 실패했습니다. 다시 시도해 주세요.', 'Login callback processing failed. Please try again.'), 'error');
        clearPendingReturnTo();
        navigate('/mypage', { replace: true });
      } finally {
        clearAuthFlow();
        isHandlingRef.current = false;
      }
    };

    processCallback(window.location.href, 'web').then(() => {
      const searchParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      if (
        searchParams.get('token') ||
        searchParams.get('error') ||
        searchParams.get('state_nonce') ||
        searchParams.get('provider') ||
        hashParams.get('token') ||
        hashParams.get('error') ||
        hashParams.get('state_nonce') ||
        hashParams.get('provider')
      ) {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }
    });

    const urlListener = addAppUrlOpenListener(({ url }) => {
      processCallback(url, 'native');
    });

    const resumeListener = addAppResumeListener(() => {
      const pendingFlow = readPendingAuthFlow();
      if (!pendingFlow || pendingFlow.status !== 'pending' || isAuthFlowExpired(pendingFlow)) {
        if (pendingFlow && isAuthFlowExpired(pendingFlow)) {
          clearAuthFlow();
        }
        return;
      }

      window.setTimeout(() => {
        const latestFlow = readPendingAuthFlow();
        if (!latestFlow || latestFlow.status !== 'pending' || isHandlingRef.current) {
          return;
        }

        markAuthFlowResult('failed', 'Browser login did not return to the app.');
        clearAuthFlow();
        clearPendingReturnTo();
        showToast(authCopy('로그인 후 앱으로 돌아오지 못했습니다. 다시 시도해 주세요.', 'Login did not return to the app. Please try again.'), 'warning');
        navigate('/mypage', { replace: true });
      }, 1200);
    });

    return () => {
      cancelled = true;
      urlListener?.remove?.();
      resumeListener?.remove?.();
    };
  }, [location, navigate, showToast]);

  return null;
}

function AppShell() {
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const hadStoredToken = hasStoredToken();
    const authState = hydrateAuthState();
    if (hadStoredToken && !authState.isAuthenticated && (authState.status === 'expired' || authState.status === 'invalid')) {
      const message = authState.status === 'expired'
        ? 'Your login session expired. Please sign in again.'
        : 'Stored login data was invalid. Please sign in again.';
      setToast({
        message,
        tone: 'warning',
        id: `startup-auth-${authState.status}`,
      });
    }
  }, []);

  useEffect(() => {
    if (!toast?.message) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setToast(null);
    }, 3200);

    return () => window.clearTimeout(timeoutId);
  }, [toast]);

  const showToast = (message, tone = 'info') => {
    setToast({
      message,
      tone,
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    });
  };

  return (
    <>
      <AnalyticsTracker />
      <AuthToast toast={toast} />
      <AuthBootstrap showToast={showToast} />
      <Routes>
        <Route path="/startus-admin/user/:userId" element={<AdminUserDetail />} />
        <Route element={<AppLayout />}>
          <Route path="/startus-admin" element={<AdminDashboard />} />
          <Route path="/" element={<Home />} />
          <Route path="/service" element={<Main />} />
          <Route path="/about" element={<About />} />
          <Route path="/insights" element={<InsightsList />} />
          <Route path="/insights/:id" element={<InsightsDetail />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/style-beta" element={<StyleBeta />} />
          <Route path="/style-beta/reward" element={<ProtectedRoute><StyleBetaReward /></ProtectedRoute>} />
          <Route path="/style-check" element={<ProtectedRoute><StyleCheck /></ProtectedRoute>} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/signup" element={<AuthPage />} />
          <Route path="/mypage" element={<MyPage />} />
          <Route path="/mypage/total-report" element={<TotalAnalysis />} />
          <Route path="/premium" element={<PaidVersion />} />
          <Route path="/premium/beyond" element={<BeyondPaidVersion />} />
          <Route path="/inside-report" element={<InsideReport />} />
          <Route path="/result/:analysisId" element={<ResultPage />} />
          <Route path="/chemistry" element={<Navigate to="/chemistry-test/info" replace />} />
          <Route
            path="/mypage/face-results/:id"
            element={(
              <ProtectedRoute>
                <FaceResultDetail />
              </ProtectedRoute>
            )}
          />
          <Route path="/main" element={<Main />} />
          <Route path="/analyze" element={<FaceResultDetail />} />
          <Route path="/info" element={<Info />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/payments/start/:productId" element={<PaymentStart />} />
          <Route path="/payments/success" element={<PaymentSuccess />} />
          <Route path="/payments/fail" element={<PaymentFail />} />
        </Route>

        <Route path="/chugumi-test" element={<ChugumiIntro />} />
        <Route path="/chugumi-test/q/:id" element={<ChugumiQuestion />} />
        <Route path="/chugumi-test/loading" element={<ChugumiLoading />} />
        <Route path="/chugumi-test/result/:id?" element={<ChugumiResult />} />

        <Route path="/character-test" element={<CharacterIntro />} />
        <Route path="/character-test/q/:id" element={<CharacterQuestion />} />
        <Route path="/character-test/result/:id?" element={<CharacterResult />} />

        <Route path="/seb-test" element={<SebIntro />} />
        <Route path="/seb-test/q/:id" element={<SebQuestion />} />
        <Route path="/seb-test/loading" element={<SebLoading />} />
        <Route path="/seb-test/result/:id?" element={<SebResult />} />

        <Route path="/chemistry-test" element={<ChemistryIntro />} />
        <Route path="/chemistry-test/info" element={<ChemistryInfo />} />
        <Route path="/chemistry-test/loading" element={<ChemistryLoading />} />
        <Route path="/chemistry-test/result" element={<ChemistryResult />} />

        <Route path="/mega-test" element={<MegaIntro />} />
        <Route path="/mega-test/q/:id" element={<MegaQuestion />} />
        <Route path="/mega-test/loading" element={<MegaLoading />} />
        <Route path="/mega-test/result/:id?" element={<MegaResult />} />

        <Route path="/beyond-test" element={<BeyondIntro />} />
        <Route path="/beyond-test/q/:id" element={<BeyondQuestion />} />
        <Route path="/beyond-test/loading" element={<BeyondLoading />} />
        <Route path="/beyond-test/result/:id?" element={<BeyondResult />} />

        <Route path="/harmony" element={<HarmonyIntro />} />
        <Route path="/harmony/lobby" element={<HarmonyLobby />} />
        <Route path="/harmony/collaborative/:id" element={<CollaborativeResult />} />
        <Route path="/harmony/select" element={<HarmonySelect />} />
        <Route path="/harmony/result" element={<HarmonyResult />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="app-shell">
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
